// Core
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  openSuccessAlert,
  openWarningAlert,
} from "state/application/applicationSlice";
import {
  useDeleteCommunityByIdMutation,
  useUpdateCommunityMutation,
  useGetCommunityByIdQuery,
} from "state/communities/communitiesApiSlice";
import { selectCurrentCommunity, setCommunity } from "state/communities/communitiesSlice";
import { selectCurrentUser } from "state/auth/authSlice";

// Components
import { AnchorNav, Crumbs, PageHead } from "components/AtomsV2";
import VersionManagementButtons from "components/VersionManagementButtons";
import LoadingCard from "components/Cards/LoadingCard";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import BasicInfo from "pages/ManageCommunities/Single/BasicInfo";
import DeleteAccount from "pages/ManageCommunities/Single/DeleteAccount";
import ExportCommunity from "pages/ManageCommunities/Single/ExportCommunity";
import Market from "pages/ManageCommunities/Single/Market";
import Settlement from "pages/ManageCommunities/Single/Settlement";
import EnergyManagementAndFlexibility from "./EnergyManagementAndFlexibility";
import ERSEConfig from "./ERSEConfig";
import DebugOptions from "./DebugOptions";

function SingleCommunity() {
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedCommunity = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);

  const canFlexibility =
    user.permissions.find((permission) => permission.name === "FLEXIBILITY_MODULE")?.is_active ||
    user.role.name === "SUPER_USER";

  const isSuperUser = user.role.name === "SUPER_USER";

  // Fetch community info by ID
  const { data: community, isLoading: isFetchingCommunity } = useGetCommunityByIdQuery(
    selectedCommunity?.id,
    {
      skip: !selectedCommunity?.id, // Only fetch if the community ID is available
    }
  );

  // PUT community
  const [updateCommunity, { isLoadingU }] = useUpdateCommunityMutation();
  const onUpdateCommunity = async (data, message) => {
    data.id = community.id;

    try {
      const newCommunity = await updateCommunity(data).unwrap();
      dispatch(setCommunity({ community: newCommunity }));

      dispatch(
        openSuccessAlert({
          title: t("basic_info.updated_title"),
          description: message,
        })
      );

      return true; // update original
    } catch (err) {
      dispatch(
        openNotificationPopup({
          notification: err,
          type: "error",
        })
      );
      return false; // don't update original
    }
  };

  // DELETE community
  const [deleteCommunity] = useDeleteCommunityByIdMutation();
  const onDeleteCommunity = async () => {
    const action = async () => {
      try {
        await deleteCommunity({ id: community.id }).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("delete.deleted_title"),
          })
        );

        navigate("/communities");
      } catch (err) {
        dispatch(
          openNotificationPopup({
            notification: err,
            type: "error",
          })
        );
      }
    };

    dispatch(openWarningAlert({ confirmText: t("delete.confirm_button"), confirmAction: action }));
  };

  const navItems = [
    { id: "general", icon: "badge", label: t("sidenav.general_info") },
    { id: "market", icon: "storefront", label: t("sidenav.internal_market") },
    { id: "settlement", icon: "receipt_long", label: t("sidenav.settlement") },
    { id: "erse", icon: "request_quote", label: t("sidenav.erse_tariffs") },
    ...(canFlexibility
      ? [{ id: "energy", icon: "bolt", label: t("sidenav.energy_mgmt_flex") }]
      : []),
    ...(isSuperUser
      ? [{ id: "debug", icon: "bug_report", label: t("sidenav.debug_options") }]
      : []),
    { id: "export", icon: "download", label: t("export.title") },
    { id: "danger", icon: "warning", label: t("sidenav.delete_community") },
  ];

  const sectionProps = community
    ? { data: community, onUpdate: onUpdateCommunity, isLoading: isLoadingU }
    : null;

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="community-info" />
      {isFetchingCommunity || !community ? (
        <LoadingCard title={t("loading.community")} />
      ) : (
        <>
          <Crumbs items={[{ label: community.name }, { label: t("config.title") }]} />
          <PageHead
            title={t("config.title")}
            subtitle={t("config.subtitle")}
            actions={
              <VersionManagementButtons
                communityId={community.id}
                entityType="config"
                entityId={community.id}
                label={community.name}
              />
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-[230px_1fr] items-start gap-rec">
            <AnchorNav items={navItems} />

            <div className="flex flex-col gap-rec min-w-0">
              <BasicInfo {...sectionProps} profileOnly />
              <Market {...sectionProps} readOnly />
              <Settlement {...sectionProps} readOnly />
              <ERSEConfig />
              {canFlexibility && <EnergyManagementAndFlexibility community={community} />}
              {isSuperUser && <DebugOptions data={community} isLoading={isLoadingU} />}
              <ExportCommunity community={community} />
              <DeleteAccount onDelete={onDeleteCommunity} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SingleCommunity;
